import { useState, useEffect } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion.esm.js';

function useCarouselAutoplay(emblaApi, autoAdvance, delay, loop) {
    const prefersReducedMotion = usePrefersReducedMotion();
    // Initialize playing state based on autoAdvance prop and reduced motion preference
    const [isPlaying, setIsPlaying] = useState(autoAdvance && !prefersReducedMotion);
    const [userManuallyPaused, setUserManuallyPaused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [manualOverride, setManualOverride] = useState(false);
    // Effect for handling auto-advance functionality
    useEffect(() => {
        if (!emblaApi || !autoAdvance || !isPlaying || isHovered)
            return;
        const interval = setInterval(() => {
            if (emblaApi.canScrollNext()) {
                emblaApi.scrollNext();
            }
            else if (loop) {
                emblaApi.scrollTo(0);
            }
            else {
                // Auto-pause when reaching the end without loop
                setIsPlaying(false);
                setUserManuallyPaused(true);
            }
        }, delay * 1000);
        return () => clearInterval(interval);
    }, [emblaApi, autoAdvance, isPlaying, delay, loop, isHovered]);
    // Effect for handling mouse enter/leave events to pause/resume playback
    useEffect(() => {
        if (!emblaApi || !autoAdvance)
            return;
        const container = emblaApi.rootNode();
        if (!container)
            return;
        // Only update hover state, not play/pause button state
        const handleMouseEnter = () => setIsHovered(true);
        const handleMouseLeave = () => setIsHovered(false);
        container.addEventListener("mouseenter", handleMouseEnter);
        container.addEventListener("mouseleave", handleMouseLeave);
        return () => {
            container.removeEventListener("mouseenter", handleMouseEnter);
            container.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [emblaApi, autoAdvance]);
    // Keep isPlaying in sync with autoAdvance prop and reduced motion preference
    // unless user has manually overridden the setting
    useEffect(() => {
        if (!manualOverride) {
            setIsPlaying(autoAdvance && !prefersReducedMotion);
            // If autoplay would be disabled due to reduced motion, mark as manually paused
            if (autoAdvance && prefersReducedMotion) {
                setUserManuallyPaused(true);
            }
        }
        // Reset manual pause state when auto-advance is disabled
        if (!autoAdvance) {
            setUserManuallyPaused(false);
            setManualOverride(false); // Reset override when autoAdvance is turned off
        }
    }, [autoAdvance, prefersReducedMotion, manualOverride]);
    const toggleAutoAdvance = () => {
        setIsPlaying((prev) => {
            const newPlayState = !prev;
            setUserManuallyPaused(!newPlayState);
            setManualOverride(true); // Mark that user has manually overridden
            return newPlayState;
        });
    };
    return {
        isPlaying,
        userManuallyPaused,
        toggleAutoAdvance,
        prefersReducedMotion,
        manualOverride,
    };
}

export { useCarouselAutoplay };
